import {toDayKey} from '../TeamActivity/constants';

export type Basis = 'daily' | 'weekly' | 'monthly';

export interface CompletionRow {
  userId: string;
  userName: string;
  day: string; // YYYY-MM-DD (UTC, matches backend grouping)
  count: number;
  lastAt: string;
}

export interface PendingRow {
  status: string;
  count: number;
  oldest: string | null;
}

export interface VettingConfig {
  trackedUserIds: string[];
  goals: {daily: number; weekly: number; monthly: number};
  completedStatus: string;
  pendingStatuses: string[];
}

export interface VettingStats {
  config: VettingConfig;
  trackedUsers: {id: string; name: string; email: string}[];
  completions: CompletionRow[];
  pending: PendingRow[];
}

export const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  entered: 'Entered',
  processing: 'Processing',
  awaiting_law_firm_info: 'Awaiting Law Firm Info',
  vetted: 'Vetted',
  closed_not_sent: 'Closed - Not Sent',
  supervisor_review: 'Supervisor Review',
  needs_further_review: 'Needs Further Review',
  hold_requests_from_ciox: 'Hold Requests from Ciox',
};

export const statusLabel = (status: string) => STATUS_LABELS[status] || status;

// Fixed-order categorical palette (CVD-validated); extra users fold into gray
export const USER_COLORS = ['#AE3EC9', '#4C6EF5', '#0CA678', '#F08C00', '#E64980'];
export const OTHER_COLOR = '#9A93A8';

export const BASIS_OPTIONS: {
  key: Basis;
  label: string;
  buckets: number;
  unit: string;
  currentLabel: string;
  windowLabel: string;
  trendLabel: string;
}[] = [
  {key: 'daily', label: 'Daily', buckets: 14, unit: 'day', currentLabel: 'Today', windowLabel: 'last 14 days', trendLabel: 'Day trend'},
  {
    key: 'weekly',
    label: 'Weekly',
    buckets: 12,
    unit: 'week',
    currentLabel: 'This week',
    windowLabel: 'last 12 weeks',
    trendLabel: 'Week trend',
  },
  {
    key: 'monthly',
    label: 'Monthly',
    buckets: 12,
    unit: 'month',
    currentLabel: 'This month',
    windowLabel: 'last 12 months',
    trendLabel: 'Month trend',
  },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const DAY_MS = 24 * 60 * 60 * 1000;

const utcMidnight = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));

// Weeks run Monday–Sunday
const mondayOf = (d: Date) => {
  const m = utcMidnight(d);
  m.setUTCDate(m.getUTCDate() - ((m.getUTCDay() + 6) % 7));
  return m;
};

const parseDayKey = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

export const fmtBucketDay = (key: string) => {
  const [, m, d] = key.split('-').map(Number);
  return `${MONTHS[m - 1]} ${d}`;
};

export interface BasisWindow {
  startDate: string;
  endDate: string;
  prevStartDate: string;
  prevEndDate: string;
  bucketLabels: string[];
  bucketCount: number;
  bucketIndexOf: (dayKey: string) => number; // -1 when outside the window
}

export function computeBasisWindow(basis: Basis, now = new Date()): BasisWindow {
  const today = utcMidnight(now);

  if (basis === 'daily') {
    const n = 14;
    const start = new Date(today.getTime() - (n - 1) * DAY_MS);
    const dayIndex: Record<string, number> = {};
    const labels: string[] = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(start.getTime() + i * DAY_MS);
      const key = toDayKey(d);
      dayIndex[key] = i;
      labels.push(fmtBucketDay(key));
    }
    const prevStart = new Date(start.getTime() - n * DAY_MS);
    return {
      startDate: start.toISOString(),
      endDate: now.toISOString(),
      prevStartDate: prevStart.toISOString(),
      prevEndDate: new Date(start.getTime() - 1).toISOString(),
      bucketLabels: labels,
      bucketCount: n,
      bucketIndexOf: (key) => (key in dayIndex ? dayIndex[key] : -1),
    };
  }

  if (basis === 'weekly') {
    const n = 12;
    const firstMonday = new Date(mondayOf(now).getTime() - (n - 1) * 7 * DAY_MS);
    const labels: string[] = [];
    for (let i = 0; i < n; i++) {
      labels.push(fmtBucketDay(toDayKey(new Date(firstMonday.getTime() + i * 7 * DAY_MS))));
    }
    const prevStart = new Date(firstMonday.getTime() - n * 7 * DAY_MS);
    return {
      startDate: firstMonday.toISOString(),
      endDate: now.toISOString(),
      prevStartDate: prevStart.toISOString(),
      prevEndDate: new Date(firstMonday.getTime() - 1).toISOString(),
      bucketLabels: labels,
      bucketCount: n,
      bucketIndexOf: (key) => {
        const idx = Math.floor((parseDayKey(key).getTime() - firstMonday.getTime()) / (7 * DAY_MS));
        return idx >= 0 && idx < n ? idx : -1;
      },
    };
  }

  // monthly
  const n = 12;
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();
  const monthIndex: Record<string, number> = {};
  const labels: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(year, month - (n - 1) + i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    monthIndex[key] = i;
    const label = MONTHS[d.getUTCMonth()];
    labels.push(i === 0 || d.getUTCMonth() === 0 ? `${label} '${String(d.getUTCFullYear()).slice(2)}` : label);
  }
  const start = new Date(Date.UTC(year, month - (n - 1), 1));
  const prevStart = new Date(Date.UTC(year, month - (2 * n - 1), 1));
  return {
    startDate: start.toISOString(),
    endDate: now.toISOString(),
    prevStartDate: prevStart.toISOString(),
    prevEndDate: new Date(start.getTime() - 1).toISOString(),
    bucketLabels: labels,
    bucketCount: n,
    bucketIndexOf: (key) => {
      const mk = key.slice(0, 7);
      return mk in monthIndex ? monthIndex[mk] : -1;
    },
  };
}

export interface UserSeries {
  userId: string;
  userName: string;
  tracked: boolean;
  color: string;
  total: number;
  perBucket: number[];
  lastAt: string | null;
}

/**
 * One series per tracked user, always present (zero-filled). Completions by
 * unselected users are excluded entirely; only when no team is configured yet
 * does everyone with completions appear, so the page is useful pre-setup.
 */
export function buildUserSeries(stats: VettingStats | undefined, win: BasisWindow): UserSeries[] {
  if (!stats) return [];
  const hasTeam = stats.trackedUsers.length > 0;
  const byUser = new Map<string, UserSeries>();

  stats.trackedUsers.forEach((u, i) => {
    byUser.set(u.id, {
      userId: u.id,
      userName: u.name || u.email,
      tracked: true,
      color: USER_COLORS[i % USER_COLORS.length],
      total: 0,
      perBucket: win.bucketLabels.map(() => 0),
      lastAt: null,
    });
  });

  stats.completions.forEach((row) => {
    let series = byUser.get(row.userId);
    if (!series) {
      if (hasTeam) return;
      series = {
        userId: row.userId,
        userName: row.userName,
        tracked: false,
        color: OTHER_COLOR,
        total: 0,
        perBucket: win.bucketLabels.map(() => 0),
        lastAt: null,
      };
      byUser.set(row.userId, series);
    }
    const idx = win.bucketIndexOf(row.day);
    if (idx >= 0) series.perBucket[idx] += row.count;
    series.total += row.count;
    if (!series.lastAt || new Date(row.lastAt) > new Date(series.lastAt)) {
      series.lastAt = row.lastAt;
    }
  });

  const sorted = Array.from(byUser.values()).sort((a, b) => {
    if (a.tracked !== b.tracked) return a.tracked ? -1 : 1;
    return b.total - a.total;
  });

  // Before a team is configured everyone would render as untracked gray;
  // color the top contributors instead — the screen's banner explains the state
  if (!hasTeam) {
    sorted.forEach((s, i) => {
      s.tracked = true;
      s.color = i < USER_COLORS.length ? USER_COLORS[i] : OTHER_COLOR;
    });
  }

  return sorted;
}

export const fmtAge = (iso: string | null) => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS);
  if (days < 1) return 'today';
  if (days < 60) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'}`;
};
